import { format } from 'date-fns';
import { formatDatetimeWithSeconds } from 'src/utils/dateUtils';
import { formatDurationMs } from 'src/utils/formatDurationMs';
import type { Span } from './types';

/** Local wall-clock label. The date is repeated only when the two ends differ. */
function clockLabel(at: Date, withDate: boolean): string {
  return format(at, withDate ? 'MMM d, yyyy · h:mm a' : 'h:mm a');
}

/**
 * Full local timestamp plus the viewer's time zone, for a hover. Formatting
 * uses the browser zone (no explicit `timeZone`), which is the user's zone.
 */
export function taskClockTitle(at: Date): string {
  const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return `${formatDatetimeWithSeconds(at)} (${zone})`;
}

export interface FinishedTaskTiming {
  kind: 'finished';
  durationLabel: string;
  rangeLabel: string | null;
  start: Date | null;
  end: Date | null;
}

export interface InProgressTaskTiming {
  kind: 'in-progress';
  start: Date;
  startLabel: string;
}

export type TaskResultTiming = FinishedTaskTiming | InProgressTaskTiming;

/** Wall-clock start: the run's start plus this span's offset on that clock. */
function measuredStart(
  span: Pick<Span, 'startMs'>,
  runStartedAt: string | null | undefined,
): Date | null {
  const runStartMs = runStartedAt ? Date.parse(runStartedAt) : Number.NaN;
  if (!Number.isFinite(runStartMs)) return null;
  return new Date(runStartMs + span.startMs);
}

function sameLocalDay(left: Date, right: Date): boolean {
  return left.toDateString() === right.toDateString();
}

/**
 * Timing for one task row on the run Details list.
 *
 * A finished task shows how long it took, and the local start–end when the
 * start was recorded. A task still running has no end, so it shows the start
 * in the viewer's time zone instead of a duration.
 */
export function taskResultTiming(
  span: Pick<Span, 'status' | 'startMs' | 'timedDurMs'>,
  runStartedAt: string | null | undefined,
): TaskResultTiming | null {
  const start = measuredStart(span, runStartedAt);
  if (span.status === 'running') {
    if (!start) return null;
    return {
      kind: 'in-progress',
      start,
      startLabel: clockLabel(start, true),
    };
  }
  const durationLabel = formatDurationMs(span.timedDurMs);
  if (!durationLabel) return null;
  if (!start || span.timedDurMs == null) {
    return {
      kind: 'finished',
      durationLabel,
      rangeLabel: null,
      start: null,
      end: null,
    };
  }
  const end = new Date(start.getTime() + span.timedDurMs);
  const withDate = !sameLocalDay(start, end);
  const rangeLabel = `${clockLabel(start, true)} – ${clockLabel(end, withDate)}`;
  return { kind: 'finished', durationLabel, rangeLabel, start, end };
}
